import { useMemo, useState } from "react";

import {
  AddAdditionalInterestBlockReason,
  EditOrReplaceAdditionalInterestBlockReason,
  EditOrReplaceOperationType,
  RemoveAdditionalInterestBlockReason,
  NonBillingAdditionalInterestInput,
  useAddAdditionalInterestMutation,
  useEditOrReplaceAdditionalInterestMutation,
  useRemoveAdditionalInterestMutation,
} from "@covertree/agent-portal-graphql";
import { Toast } from "@covertree/utils";
import { states } from "@covertree/consts";
import { AdditionalInterestType } from "@covertree/new-business-graphql";
import { countInterestsByType } from "@covertree/features";

import {
  AdditionalInterestFormData,
  DefaultErrors,
  ModalMode,
  MutationBlockReason,
  SuccessModalData,
  UseAdditionalInterestFormParams,
} from "./types";
import { DEFAULT_ERROR_MESSAGE } from "./consts";
import { usePolicyDetails } from "../../../PolicyDetailsContext";

const buildAdditionalInterestInput = (
  data: AdditionalInterestFormData
): NonBillingAdditionalInterestInput => ({
  name: data.name,
  type: data.type as AdditionalInterestType,
  streetAddress: data.streetAddress,
  lotUnit: data.lotUnit || null,
  city: data.city,
  state: data.state.value,
  zipCode: data.zipCode,
  email: data.email || null,
  accountNumber: data.accountNumber || null,
});

export function useAdditionalInterestForm({
  policy,
  validationReason,
}: UseAdditionalInterestFormParams) {
  const [formMode, setFormMode] = useState<ModalMode>(null);
  const [selectedInterestId, setSelectedInterestId] = useState<string | null>(
    null
  );
  const [removeInterestId, setRemoveInterestId] = useState<string | null>(null);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [mutationBlockReason, setMutationBlockReason] =
    useState<MutationBlockReason | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successModalData, setSuccessModalData] = useState<SuccessModalData>();
  const { refetchPolicy } = usePolicyDetails() ?? {};
  const [addAdditionalInterest, { loading: isAddLoading }] =
    useAddAdditionalInterestMutation();
  const [editAdditionalInterest, { loading: isEditLoading }] =
    useEditOrReplaceAdditionalInterestMutation();
  const [removeAdditionalInterest, { loading: isRemoveLoading }] =
    useRemoveAdditionalInterestMutation();

  const additionalInterests = policy?.additionalInterests ?? [];
  const isMutationLoading = isAddLoading || isEditLoading || isRemoveLoading;

  const selectedInterest = additionalInterests?.find(
    (ai) => ai.additionalInterestId === selectedInterestId
  );

  const interestToRemove = additionalInterests?.find(
    (ai) => ai.additionalInterestId === removeInterestId
  );

  const interestsCountByType = useMemo(
    () => countInterestsByType(additionalInterests),
    [additionalInterests]
  );

  const handleAddClick = () => {
    if (validationReason) {
      setMutationBlockReason(validationReason);

      return;
    }

    setSelectedInterestId(null);
    setFormMode("add");
  };

  const handleEditClick = (id: string) => {
    if (validationReason) {
      setMutationBlockReason(validationReason);

      return;
    }

    setSelectedInterestId(id);
    setFormMode("edit");
  };

  const handleReplaceClick = (id: string) => {
    if (validationReason) {
      setMutationBlockReason(validationReason);

      return;
    }

    setSelectedInterestId(id);
    setFormMode("replace");
  };

  const handleFormClose = () => {
    if (isMutationLoading) {
      return;
    }

    setFormMode(null);
    setSelectedInterestId(null);
  };

  const handleFormSubmit = async (data: AdditionalInterestFormData) => {
    if (!policy) {
      setMutationBlockReason(DefaultErrors.NotKnownError);

      return;
    }

    const additionalInterest = buildAdditionalInterestInput(data);

    try {
      let successData: SuccessModalData | undefined;
      let reason:
        | AddAdditionalInterestBlockReason
        | EditOrReplaceAdditionalInterestBlockReason
        | null
        | undefined;
      let isSuccessful: boolean | null | undefined;

      if (formMode === "add") {
        const { data: response } = await addAdditionalInterest({
          variables: {
            policyLocator: policy.policyLocator,
            additionalInterest,
          },
        });

        const result = response?.addAdditionalInterest;
        if (result) {
          successData = {
            isOfferSent: result.isOfferSent,
            isRenewalActive: result.isRenewalActive,
          };
          reason = result.reason;
          isSuccessful = result.isSuccessful;
        }
      } else if (
        (formMode === "edit" || formMode === "replace") &&
        selectedInterestId
      ) {
        const { data: response } = await editAdditionalInterest({
          variables: {
            policyLocator: policy.policyLocator,
            additionalInterestId: selectedInterestId,
            additionalInterest,
            operationType: EditOrReplaceOperationType.Edit,
          },
        });

        const result = response?.editOrReplaceAdditionalInterest;
        if (result) {
          successData = {
            isOfferSent: result.isOfferSent,
            isRenewalActive: result.isRenewalActive,
          };
          reason = result.reason;
          isSuccessful = result.isSuccessful;
        }
      }

      if (isSuccessful) {
        setIsSuccessModalOpen(true);
        setSuccessModalData(successData);
        refetchPolicy?.();
      } else {
        if (
          reason &&
          [
            AddAdditionalInterestBlockReason.WebAppApiRequestFailed,
            AddAdditionalInterestBlockReason.WebAppApiRequestNotValid,
            EditOrReplaceAdditionalInterestBlockReason.WebAppApiRequestFailed,
            EditOrReplaceAdditionalInterestBlockReason.WebAppApiRequestNotValid,
          ].includes(reason)
        ) {
          Toast.showError(DEFAULT_ERROR_MESSAGE);

          return;
        }

        setMutationBlockReason(reason || DefaultErrors.NotKnownError);
      }

      handleFormClose();
    } catch {
      Toast.showError(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleRemoveClick = (id: string) => {
    if (validationReason) {
      setMutationBlockReason(validationReason);

      return;
    }

    setRemoveInterestId(id);
    setRemoveModalOpen(true);
  };

  const handleRemoveClose = () => {
    if (isMutationLoading) {
      return;
    }

    setRemoveModalOpen(false);
    setRemoveInterestId(null);
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };

  const handleRemoveConfirm = async () => {
    if (!policy || !removeInterestId) {
      Toast.showError(DEFAULT_ERROR_MESSAGE);

      return;
    }

    try {
      const { data: response } = await removeAdditionalInterest({
        variables: {
          policyLocator: policy.policyLocator,
          additionalInterestId: removeInterestId,
        },
      });

      const result = response?.removeAdditionalInterest;

      if (result?.isSuccessful) {
        setIsSuccessModalOpen(true);
        setSuccessModalData({
          isOfferSent: result.isOfferSent,
          isRenewalActive: result.isRenewalActive,
        });
        refetchPolicy?.();
      } else {
        const reason = result?.reason;

        if (
          reason &&
          [
            RemoveAdditionalInterestBlockReason.WebAppApiRequestFailed,
            RemoveAdditionalInterestBlockReason.WebAppApiRequestNotValid,
          ].includes(reason)
        ) {
          Toast.showError(DEFAULT_ERROR_MESSAGE);

          return;
        }

        setMutationBlockReason(reason || DefaultErrors.NotKnownError);
      }

      handleRemoveClose();
    } catch {
      Toast.showError(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handleBlockReasonModalClose = () => setMutationBlockReason(null);

  const formSelectedInterest = useMemo<AdditionalInterestFormData | null>(
    () =>
      selectedInterest
        ? {
            name: selectedInterest.name,
            type: selectedInterest.type,
            streetAddress: selectedInterest.streetAddress,
            lotUnit: selectedInterest.lotUnit ?? "",
            city: selectedInterest.city,
            state: states.find((s) => s.value === selectedInterest.state) ?? {
              label: "",
              value: "",
            },
            zipCode: selectedInterest.zipCode.replace(/\D/g, ""),
            email: selectedInterest.email ?? "",
            accountNumber: selectedInterest.accountNumber ?? "",
          }
        : null,
    [selectedInterest]
  );

  return {
    formMode,
    successModalData,
    formSelectedInterest,
    interestsCountByType,
    isMutationLoading,
    mutationBlockReason,
    interestToRemove,
    removeModalOpen,
    isSuccessModalOpen,
    handleAddClick,
    handleEditClick,
    handleReplaceClick,
    handleFormClose,
    handleFormSubmit,
    handleRemoveClick,
    handleRemoveClose,
    handleRemoveConfirm,
    handleBlockReasonModalClose,
    handleCloseSuccessModal,
  };
}
